'use client'

import { useState, useEffect, FormEvent, useRef } from 'react'
import { X, Save, Building2, Sparkles, Loader2, Search } from 'lucide-react'
import { panel, panelInner, headingClass, mutedClass, ghostBtn, primaryBtn, primaryBtnInline, fieldClass, labelClass, errorBox, successBox } from './ui'

interface CompanyProfileFormProps {
  userId: string
  onSuccess: () => void
  onCancel: () => void
}

const INDUSTRIES = [
  'Technology',
  'Software Development',
  'Information Technology (IT)',
  'Cybersecurity',
  'Artificial Intelligence (AI)',
  'Cloud Computing',
  'Telecommunications',
  'Healthcare',
  'Pharmaceuticals',
  'Medical Devices',
  'Biotechnology',
  'Health Services',
  'Mental Health',
  'Veterinary Services',
  'Finance',
  'Banking',
  'Insurance',
  'Investment Management',
  'Accounting',
  'Financial Services',
  'Cryptocurrency',
  'Retail',
  'Fashion & Apparel',
  'Consumer Goods',
  'Luxury Goods',
  'E-commerce',
  'Online Marketplace',
  'Education',
  'E-Learning',
  'Training & Development',
  'Educational Technology',
  'Manufacturing',
  'Automotive',
  'Aerospace',
  'Industrial Manufacturing',
  'Textile Manufacturing',
  'Real Estate',
  'Property Development',
  'Construction',
  'Architecture',
  'Interior Design',
  'Food & Beverage',
  'Restaurants',
  'Catering',
  'Food Production',
  'Beverage Production',
  'Entertainment',
  'Media & Broadcasting',
  'Film & Television',
  'Music',
  'Gaming',
  'Sports & Recreation',
  'Consulting',
  'Management Consulting',
  'Legal Services',
  'Marketing & Advertising',
  'Public Relations',
  'Digital Marketing',
  'Non-profit',
  'Charity',
  'Social Services',
  'Clean Energy',
  'Renewable Energy',
  'Solar Energy',
  'Wind Energy',
  'Environmental Services',
  'Transportation & Logistics',
  'Shipping & Freight',
  'Airlines',
  'Travel & Tourism',
  'Hospitality',
  'Hotels & Accommodation',
  'Agriculture',
  'Farming',
  'AgriTech',
  'Food Processing',
  'Energy & Utilities',
  'Oil & Gas',
  'Electric Utilities',
  'Water & Waste Management',
  'Mining',
  'Chemical Industry',
  'Pharmaceutical Manufacturing',
  'Beauty & Cosmetics',
  'Personal Care',
  'Fitness & Wellness',
  'Fashion & Design',
  'Jewelry',
  'Luxury Brands',
  'Automotive Parts',
  'Publishing',
  'Print Media',
  'Digital Media',
  'Content Creation',
  'Social Media',
  'Professional Services',
  'Human Resources',
  'Recruitment',
  'Engineering',
  'Civil Engineering',
  'Mechanical Engineering',
  'Electrical Engineering',
  'Research & Development',
  'Scientific Services',
  'Laboratory Services',
  'Government & Public Sector',
  'Defense & Security',
  'Public Administration',
  'Other',
]

const COUNTRIES = [
  'United States',
  'United Kingdom',
  'Canada',
  'Australia',
  'Germany',
  'France',
  'Italy',
  'Spain',
  'Netherlands',
  'Belgium',
  'Switzerland',
  'Austria',
  'Sweden',
  'Norway',
  'Denmark',
  'Finland',
  'Poland',
  'Portugal',
  'Greece',
  'Ireland',
  'Czech Republic',
  'Hungary',
  'Romania',
  'Algeria',
  'Morocco',
  'Tunisia',
  'Egypt',
  'South Africa',
  'Nigeria',
  'Kenya',
  'Ghana',
  'Senegal',
  'Ivory Coast',
  'Cameroon',
  'Mali',
  'Burkina Faso',
  'Niger',
  'Chad',
  'Mauritania',
  'China',
  'Japan',
  'South Korea',
  'India',
  'Thailand',
  'Vietnam',
  'Indonesia',
  'Philippines',
  'Malaysia',
  'Singapore',
  'Brazil',
  'Argentina',
  'Chile',
  'Colombia',
  'Peru',
  'Mexico',
  'Venezuela',
  'Saudi Arabia',
  'United Arab Emirates',
  'Qatar',
  'Kuwait',
  'Turkey',
  'Russia',
  'Ukraine',
  'Israel',
  'New Zealand',
  'Other',
]

export default function CompanyProfileForm({ userId, onSuccess, onCancel }: CompanyProfileFormProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [profile, setProfile] = useState<any>(null)
  const [aiLoading, setAiLoading] = useState<{
    description: boolean
    target_market: boolean
    brand_values: boolean
  }>({
    description: false,
    target_market: false,
    brand_values: false,
  })

  const descriptionRef = useRef<HTMLTextAreaElement>(null)
  const targetMarketRef = useRef<HTMLTextAreaElement>(null)
  const brandValuesRef = useRef<HTMLTextAreaElement>(null)
  const competitorsRef = useRef<HTMLTextAreaElement>(null)
  const [competitorsLoading, setCompetitorsLoading] = useState(false)

  useEffect(() => {
    loadProfile()
  }, [userId])

  const loadProfile = async () => {
    try {
      const response = await fetch('/api/brief613/company-profile', {
        headers: {
          'x-user-id': userId,
        },
      })
      const data = await response.json()
      if (data.success && data.profile) {
        setProfile(data.profile)
      }
    } catch (error) {
      console.error('Error loading profile:', error)
    }
  }

  const getFormData = (form: HTMLFormElement) => {
    const formData = new FormData(form)
    const data: any = {}
    for (const [key, value] of formData.entries()) {
      data[key] = value.toString().trim()
    }
    return data
  }

  const findCompetitors = async () => {
    const form = document.querySelector('form') as HTMLFormElement
    if (!form) return

    const formData = getFormData(form)
    const industry = formData.industry
    const country = formData.country
    const companyName = formData.company_name

    if (!industry || !country) {
      setError('Please select industry and country first')
      return
    }

    if (!competitorsRef.current) return

    setCompetitorsLoading(true)
    setError('')

    try {
      const response = await fetch('/api/brief613/find-competitors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
        body: JSON.stringify({
          industry,
          country,
          companyName,
        }),
      })

      const result = await response.json()

      if (result.success && result.data?.competitors) {
        competitorsRef.current.value = result.data.competitors
        // Trigger input event to update form state
        competitorsRef.current.dispatchEvent(new Event('input', { bubbles: true }))
      } else {
        setError(result.error || 'Failed to find competitors. Please try again.')
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while finding competitors')
    } finally {
      setCompetitorsLoading(false)
    }
  }

  const enhanceField = async (field: 'description' | 'target_market' | 'brand_values') => {
    const form = document.querySelector('form') as HTMLFormElement
    if (!form) return

    const formData = getFormData(form)
    const fieldRefs = {
      description: descriptionRef,
      target_market: targetMarketRef,
      brand_values: brandValuesRef,
    }

    const fieldRef = fieldRefs[field]
    if (!fieldRef.current) return

    const originalText = fieldRef.current.value.trim()

    setAiLoading((prev) => ({ ...prev, [field]: true }))
    setError('')

    try {
      const response = await fetch('/api/brief613/enhance-profile-field', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
        body: JSON.stringify({
          field,
          originalText,
          formData,
        }),
      })

      const result = await response.json()

      if (result.success && result.data?.enhancedText) {
        fieldRef.current.value = result.data.enhancedText
        // Trigger input event to update form state
        fieldRef.current.dispatchEvent(new Event('input', { bubbles: true }))
      } else {
        setError(result.error || 'Failed to enhance text. Please try again.')
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while enhancing text')
    } finally {
      setAiLoading((prev) => ({ ...prev, [field]: false }))
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    const formData = new FormData(e.currentTarget)
    const data: any = {}

    for (const [key, value] of formData.entries()) {
      data[key] = value.toString().trim()
    }

    // Validate required fields
    if (!data.company_name || !data.industry || !data.country || !data.description || !data.target_market) {
      setError('Please fill in all required fields')
      setLoading(false)
      return
    }

    try {
      const response = await fetch('/api/brief613/company-profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
        body: JSON.stringify(data),
      })

      const result = await response.json()

      if (result.success) {
        onSuccess()
      } else {
        setError(result.error || 'Failed to save profile')
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={`${panel} p-8 max-w-4xl mx-auto`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-base font-semibold text-white/90 sm:text-lg">Company Profile</h2>
        <button onClick={onCancel} className="text-white/60 hover:text-white transition-colors">
          <X size={24} className="text-white/50" />
        </button>
      </div>

      <div className="text-center mb-6">
        <Building2 className="mx-auto mb-4 text-white/50" size={32} />
        <h3 className="text-base font-semibold text-white/90">Complete Your Company Profile</h3>
        <p className="text-white/70 mt-2">This information helps us generate better AI suggestions for your briefs</p>
      </div>

      {error && (
        <div className={`mb-6 ${errorBox}`}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className={labelClass}>Company Name *</label>
            <input
              type="text"
              name="company_name"
              required
              defaultValue={profile?.companyName || ''}
              className={fieldClass}
              disabled={loading}
            />
          </div>

          <div>
            <label className={labelClass}>Industry *</label>
            <select
              name="industry"
              required
              defaultValue={profile?.industry || ''}
              className={fieldClass}
              disabled={loading}
            >
              <option value="">Select industry</option>
              {INDUSTRIES.map((industry, index) => (
                <option key={`${industry}-${index}`} value={industry}>
                  {industry}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Country *</label>
            <select
              name="country"
              required
              defaultValue={profile?.country || ''}
              className={fieldClass}
              disabled={loading}
            >
              <option value="">Select country</option>
              {COUNTRIES.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Website</label>
            <input
              type="url"
              name="website"
              defaultValue={profile?.website || ''}
              className={fieldClass}
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={labelClass}>Description *</label>
            <button
              type="button"
              onClick={() => enhanceField('description')}
              disabled={loading || aiLoading.description}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-white/10 hover:bg-white/20 text-white/90 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Enhance with AI"
            >
              {aiLoading.description ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Enhancing...
                </>
              ) : (
                <>
                  <Sparkles size={14} />
                  Enhance with AI
                </>
              )}
            </button>
          </div>
          <textarea
            ref={descriptionRef}
            name="description"
            required
            rows={4}
            defaultValue={profile?.description || ''}
            className={`${fieldClass} h-auto min-h-[5rem] py-2`}
            placeholder="What does your company do?"
            disabled={loading || aiLoading.description}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={labelClass}>Target Market *</label>
            <button
              type="button"
              onClick={() => enhanceField('target_market')}
              disabled={loading || aiLoading.target_market}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-white/10 hover:bg-white/20 text-white/90 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Enhance with AI"
            >
              {aiLoading.target_market ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Enhancing...
                </>
              ) : (
                <>
                  <Sparkles size={14} />
                  Enhance with AI
                </>
              )}
            </button>
          </div>
          <textarea
            ref={targetMarketRef}
            name="target_market"
            required
            rows={3}
            defaultValue={profile?.targetMarket || ''}
            className={`${fieldClass} h-auto min-h-[5rem] py-2`}
            placeholder="Who are your target customers?"
            disabled={loading || aiLoading.target_market}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={labelClass}>Brand Values</label>
            <button
              type="button"
              onClick={() => enhanceField('brand_values')}
              disabled={loading || aiLoading.brand_values}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-white/10 hover:bg-white/20 text-white/90 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Enhance with AI"
            >
              {aiLoading.brand_values ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Enhancing...
                </>
              ) : (
                <>
                  <Sparkles size={14} />
                  Enhance with AI
                </>
              )}
            </button>
          </div>
          <textarea
            ref={brandValuesRef}
            name="brand_values"
            rows={3}
            defaultValue={profile?.brandValues || ''}
            className={`${fieldClass} h-auto min-h-[5rem] py-2`}
            placeholder="What are your brand values and tone?"
            disabled={loading || aiLoading.brand_values}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={labelClass}>Competitors</label>
            <button
              type="button"
              onClick={findCompetitors}
              disabled={loading || competitorsLoading}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-white/10 hover:bg-white/20 text-white/90 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Find competitors using AI web search"
            >
              {competitorsLoading ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search size={14} className="text-white/50" />
                  Find Competitors
                </>
              )}
            </button>
          </div>
          <textarea
            ref={competitorsRef}
            name="competitors"
            rows={4}
            defaultValue={profile?.competitors || ''}
            className={`${fieldClass} h-auto min-h-[5rem] py-2`}
            placeholder="Who are your main competitors? Click 'Find Competitors' to search automatically."
            disabled={loading || competitorsLoading}
          />
        </div>

        <div className="flex justify-end gap-4">
          <button type="button" onClick={onCancel} className={ghostBtn} disabled={loading}>
            Cancel
          </button>
          <button type="submit" className={primaryBtnInline} disabled={loading}>
            {loading ? 'Saving...' : (
              <>
                <Save size={18} className="text-white/50" />
                Save Profile
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

